import { ViewChild, ElementRef } from '@angular/core';

import { Observable, combineLatest, firstValueFrom, of } from 'rxjs';
import {
  startWith,
  map,
  distinctUntilChanged,
  switchMap,
  filter,
  debounceTime,
} from 'rxjs/operators';
import {
  Component,
  OnInit,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ButtonGroupItem } from '@app/shared/interfaces/common.model';
import { AssetManagementService } from '../../asset-management-service';
import { MyRequestTableService } from '../../asset-management-table/my-request-table.service';
// import { MemoizedApiService } from '@app/shared/services/memoized-api.service';
import { ToastrService } from 'ngx-toastr';
import {
  DropdownOption,
  LOCAL_ACCESSORY_OPTIONS,
  LOCATION_OPTIONS,
  INITIAL_LAPTOP_TYPE_OPTIONS,
  RESOURCE_FIELDS,
} from './my-request-form.constants';
import { HiringRequestService } from '@app/pages/hiring-manager/hiring-request/hiring-request.service';
import { DateUtils } from '@utils/date.utils';

@Component({
  selector: 'app-my-request-form',
  templateUrl: './my-request-form.component.html',
  styleUrl: './my-request-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyRequestFormComponent implements OnInit {
  localAccessoryOptions = LOCAL_ACCESSORY_OPTIONS;

  accessoryOptions: DropdownOption[] = this.localAccessoryOptions;
  duplicateEmailError: string | null = null;
  @ViewChild('emailAutoInput') emailAutoInput!: ElementRef<HTMLInputElement>;
  locations = new FormControl<string>('', { nonNullable: true });
  toppingList: string[] = [];
  trackTopping = (index: number, item: string) => item;
  isModal: boolean = false;
  displayManagerFn(option: any): string {
    return option?.label ?? '';
  }
  requestForm!: FormGroup;
  filteredOptions!: Observable<string[]>;
  myControl = new FormControl('');
  options: any[] = [];
  displayEmailFn(option: any): string {
    return option?.label || option?.value || option || '';
  }
  private readonly resourceFields: string[] = RESOURCE_FIELDS;
  isEditMode: boolean = false;
  formData: any = null;
  toEmails: string[] = [];
  ccEmails: string[] = [];
  currentLocation: string = '';
  locationOptions: DropdownOption[] = LOCATION_OPTIONS.filter(
    (option) => option.value !== 'All',
  );
  selectedFile: string | null = null;

  leaderOptions$!: Observable<any[]>;
  managerOptions$!: Observable<any[]>;
  filteredManagerOptions$!: Observable<any[]>;
  filteredLeaderOptions$!: Observable<any[]>;
  private selectedEmployeeEmail: string | null = null;
  osTypeOptions: DropdownOption[] = [];
  brandNameOptions: DropdownOption[] = [];
  laptopTypeOptions: DropdownOption[] = [];
  accessoryOptions$: Observable<DropdownOption[]> = of([]);

  submitButton: ButtonGroupItem = {
    label: 'Submit Request',
    buttonType: 'Primary',
    disabled: false,
    icon: '',
  };

  employeeDataMap: Map<string, any> = new Map();
  constructor(
    private readonly fb: FormBuilder,
    private readonly assetManagementService: AssetManagementService,
    private readonly myRequestTableService: MyRequestTableService,
    private readonly cdr: ChangeDetectorRef,
    // private readonly memoizedApiService: MemoizedApiService,
    private readonly toastr: ToastrService,
    public hiringRequestService: HiringRequestService,
    public dialogRef?: MatDialogRef<MyRequestFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: any,
  ) {}

  async ngOnInit(): Promise<void> {
    this.initializeComponentState();

    this.buildForm();
    await this.setupAccessoryOptions$();

    this.requestForm.get('location')?.valueChanges.subscribe((val) => {
      this.locations.setValue(val || '', { emitEvent: false });
    });
    this.locations.valueChanges.subscribe((val) => {
      this.requestForm
        .get('location')
        ?.setValue(val || '', { emitEvent: false });
    });

    // Use cached observables from service
    this.leaderOptions$ = this.myRequestTableService.leaderOptions$;
    this.managerOptions$ = this.myRequestTableService.managerOptions$;

    this.filteredLeaderOptions$ = combineLatest([
      this.leaderOptions$,
      this.requestForm.get('DnTLeader')!.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([options, value]) => {
        const filterValue =
          typeof value === 'string'
            ? value.toLowerCase()
            : value?.label?.toLowerCase() || '';
        return options.filter((option: any) =>
          option.label.toLowerCase().includes(filterValue),
        );
      }),
    );

    this.filteredManagerOptions$ = combineLatest([
      this.managerOptions$,
      this.requestForm
        .get('reportingManager')!
        .valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([options, value]) => {
        const filterValue =
          typeof value === 'string'
            ? value.toLowerCase()
            : value?.label?.toLowerCase() || '';
        return options.filter((option) =>
          option.label.toLowerCase().includes(filterValue),
        );
      }),
    );

    this.setupDebouncedEmailSearch();
    if (!this.isEditMode) {
      const userProfile = sessionStorage.getItem('userProfile');
      let city = 'Hyderabad'; // Default fallback
      if (userProfile) {
        try {
          const profileData = JSON.parse(userProfile);
          city = profileData?.city || 'Hyderabad';
        } catch (error) {
          console.error(
            'Error parsing userProfile from session storage:',
            error,
          );
        }
      }

      this.loadEmailNotifications(city);
    }

    this.submitButton.label = this.isEditMode
      ? 'Update Request'
      : 'Submit Request';
    this.fetchAccessoriesDetails('', '', 1).then(() => {
      if (this.isEditMode) {
        this.onEdit();
      }
    });
  }

  private setupDebouncedEmailSearch(): void {
    this.myControl.valueChanges
      .pipe(
        filter((val) => typeof val === 'string' && val !== null),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          if (searchTerm.trim().length < 2) {
            return of({ data: [] });
          }
          return this.assetManagementService.searchEmployeesByEmail(
            searchTerm.trim(),
          );
        }),
      )
      .subscribe({
        next: (response: any) => {
          const employees = response?.data || response || [];
          this.employeeDataMap.clear();
          this.options = employees?.map((employee: any) => {
            this.employeeDataMap.set(employee.email, employee);
            return {
              label: employee.email,
              value: employee.email,
            };
          });
          this.cdr.markForCheck();
        },
        error: () => {
          this.options = [];
          this.cdr.markForCheck();
        },
      });
  }

  private _filterOptions(value: string): string[] {
    const filterValue = value?.toLowerCase() || '';
    return this.options.filter((option) =>
      option.toLowerCase().includes(filterValue),
    );
  }

  private async onEdit(): Promise<void> {
    if (this.formData) {
      await this.patchFormForEdit(this.formData);
    }
  }

  displayLeaderFn(option: any): string {
    return option?.label ?? '';
  }

  private initializeComponentState(): void {
    if (this.data) {
      this.isModal = this.data.isModal || false;
      this.isEditMode = this.data.isEditMode || false;
      this.formData = this.data.formData || null;
    }
  }

  private buildForm(): void {
    this.requestForm = this.fb.group({
      dateOfPickup: ['', Validators.required],
      location: ['', Validators.required],
      toEmailIds: [[], Validators.required],
      ccEmailIds: [[]],
      email: ['', [Validators.required, Validators.email]],
      dateOfJoining: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      DnTLeader: ['', Validators.required],
      reportingManager: ['', Validators.required],
      osType: ['', Validators.required],
      laptopType: ['', Validators.required],
      brandName: ['', Validators.required],
      laptopSerialNumber: [''],
      chargerSerialNumber: [''],
      status: ['initiated', Validators.required],
      accessoriesType: [[], Validators.required],
      raiseAssetRequestFileName: [''],
      employeeId: ['', Validators.required],
      comments: [''],
      cancellationReason: [''],
    });

    // Set up dynamic validators
    this.requestForm.get('status')?.valueChanges.subscribe(() => {
      this.updateSerialValidators();
      this.updateCommentsValidator();
    });
    this.requestForm.get('osType')?.valueChanges.subscribe(() => {
      this.updateAttachmentValidator();
    });
    this.updateSerialValidators();
    this.updateAttachmentValidator();
    this.updateCommentsValidator();
  }

  private updateSerialValidators(): void {
    const status = this.requestForm.get('status')?.value?.toLowerCase();
    const laptopSerial = this.requestForm.get('laptopSerialNumber');
    const chargerSerial = this.requestForm.get('chargerSerialNumber');
    if (status === 'ready' || status === 'delivered') {
      laptopSerial?.setValidators([Validators.required]);
      chargerSerial?.setValidators([Validators.required]);
    } else {
      laptopSerial?.clearValidators();
      chargerSerial?.clearValidators();
    }
    laptopSerial?.updateValueAndValidity();
    chargerSerial?.updateValueAndValidity();
  }

  private updateAttachmentValidator(): void {
    const osType = this.requestForm.get('osType')?.value?.toLowerCase();
    const attachment = this.requestForm.get('raiseAssetRequestFileName');
    if (osType.includes('mac')) {
      attachment?.setValidators([Validators.required]);
    } else {
      attachment?.clearValidators();
    }
    attachment?.updateValueAndValidity();
  }

  private updateCommentsValidator(): void {
    const status = this.requestForm.get('status')?.value?.toLowerCase();
    const comments = this.requestForm.get('comments');
    if (status === 'cancelled') {
      comments?.setValidators([Validators.required]);
    } else {
      comments?.clearValidators();
    }
    comments?.updateValueAndValidity();
  }

  private disableResourceFields(): void {
    for (const field of this.resourceFields) {
      this.requestForm.get(field)?.disable();
    }
    // Disable location and date of pickup in edit mode
    this.requestForm.get('location')?.disable();
  }

  private findOptionValue(options: any[], value: any): string {
    if (!value || !options) return '';
    const found = options.find(
      (opt) =>
        opt.value === value ||
        (opt.label && opt.label.toLowerCase() === String(value).toLowerCase()),
    );
    return found ? found.value : '';
  }

  onFileSelected(file: File) {
    this.requestForm.get('raiseAssetRequestFileName')?.setValue(file);
    this.cdr.markForCheck();
  }

  private async patchFormForEdit(data: any): Promise<void> {
    if (!data || !this.requestForm) return;

    // Bind email arrays directly from data
    this.toEmails = Array.isArray(data.toEmailIds) ? data.toEmailIds : [];
    this.ccEmails = Array.isArray(data.ccEmailIds) ? data.ccEmailIds : [];

    this.selectedFile = data?.approvalConfirmationUrl
      ? (data?.approvalConfirmationUrl.split('/').pop() ?? null)
      : null;

    let locationValue = '';
    if (data.location) {
      locationValue = this.findOptionValue(this.locationOptions, data.location);
    }

    let dntLeaderObj = null;
    try {
      const leaderOptions = await firstValueFrom(this.filteredLeaderOptions$);
      dntLeaderObj =
        leaderOptions.find(
          (option) =>
            (option?.label &&
              option.label.trim().toLowerCase() ===
                String(data.dntLeader).trim().toLowerCase()) ||
            (option?.value &&
              option.value.trim().toLowerCase() ===
                String(data.dntLeader).trim().toLowerCase()),
        ) || null;
    } catch (e) {
      dntLeaderObj = null;
    }
    let reportingManagerObj = null;
    try {
      const managerOptions = await firstValueFrom(this.filteredManagerOptions$);
      reportingManagerObj =
        managerOptions.find(
          (option) =>
            (option?.label &&
              option.label.trim().toLowerCase() ===
                String(data.reportingManager).trim().toLowerCase()) ||
            (option?.value &&
              option.value.trim().toLowerCase() ===
                String(data.reportingManager).trim().toLowerCase()),
        ) || null;
    } catch (e) {
      reportingManagerObj = null;
    }

    const statusValue = data.status || 'initiated';
    let accessoriesTypeValues: string[] = [];
    if (Array.isArray(data.accessoriesType)) {
      accessoriesTypeValues = data.accessoriesType
        .flatMap((val: any) => {
          const stringVal = String(val).trim();
          if (stringVal.includes(',')) {
            return stringVal
              .split(',')
              .map((item) => item.trim())
              .filter((item) => item.length > 0);
          }
          return stringVal;
        })
        .filter((item: string) => item.length > 0);
    } else if (data.accessoriesType) {
      const accessoriesString = String(data.accessoriesType).trim();
      if (accessoriesString.includes(',')) {
        accessoriesTypeValues = accessoriesString
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      } else {
        accessoriesTypeValues = [accessoriesString];
      }
    }
    if (data.osType) {
      await this.fetchAccessoriesDetails(data.osType, '', 1);
    }

    if (data.osType && data.brandName) {
      await this.fetchAccessoriesDetails(data.osType, data.brandName, 1);

      if (!this.brandNameOptions.find((opt) => opt.value === data.brandName)) {
        this.brandNameOptions.push({
          label: data.brandName,
          value: data.brandName,
        });
      }
    }
    const laptopTypeValue: string =
      this.laptopTypeOptions.find((option: any) => {
        return (
          option.value.trim() === String(data.laptopType).trim() ||
          option.label.trim() === String(data.laptopType).trim()
        );
      })?.value ||
      data.laptopType ||
      '';

    const patchData = {
      dateOfPickup: data.dateOfPickup ? DateUtils.parseLocalDate(data.dateOfPickup) : '',
      location: locationValue,
      email: data.email || '',
      dateOfJoining: data.lastHireDate || '',
      firstName: data.firstName,
      lastName: data.lastName,
      DnTLeader: dntLeaderObj,
      reportingManager: reportingManagerObj,
      osType: data.osType || '',
      laptopType: laptopTypeValue,
      brandName: data.brandName || '',
      laptopSerialNumber: data.laptopSerialNumber || '',
      chargerSerialNumber: data.chargerSerialNumber || '',
      status: statusValue,
      accessoriesType: accessoriesTypeValues,
      employeeId: data.employeeId || '',
      comments: data.cancellationReason || data.comments || '',
      raiseAssetRequestFileName: data.approvalConfirmationUrl || '',
      cancellationReason: data.cancellationReason || '',
      toEmailIds: this.toEmails,
      ccEmailIds: this.ccEmails,
    };

    if (locationValue) {
      this.locations.setValue(locationValue, { emitEvent: false });
      this.requestForm
        .get('location')
        ?.setValue(locationValue, { emitEvent: false });
    }

    this.requestForm.patchValue(patchData);

    this.disableResourceFields();
    this.cdr.markForCheck();
  }

  private getAssetRequestPayload(
    formValue: any,
    leaderEmail: string,
    managerEmail: string,
  ) {
    const extractEmail = (field: any) => {
      if (!field) return '';
      if (typeof field === 'string') return field;
      if (typeof field === 'object' && field.value) return field.value;
      return '';
    };

    return {
      location:
        formValue.location &&
        typeof formValue.location === 'object' &&
        formValue.location.value
          ? formValue.location.value
          : formValue.location,
      toEmailIds: Array.isArray(formValue.toEmailIds)
        ? formValue.toEmailIds
        : [],
      ccEmailIds: Array.isArray(formValue.ccEmailIds)
        ? formValue.ccEmailIds
        : [],
      resourceInfoDto: {
        email: formValue.email,
        lastHireDate: DateUtils.formatDateToString(formValue.dateOfJoining),
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        DnTLeader: extractEmail(formValue.DnTLeader),
        reportingManager: extractEmail(formValue.reportingManager),
        employeeId: formValue.employeeId,
      },
      accessoriesInfoDto: {
        status: formValue.status,
        osType: formValue.osType,
        BrandName: formValue.brandName || '',
        laptopType: formValue.laptopType,
        laptopSerialNumber: formValue.laptopSerialNumber,
        chargerSerialNumber: formValue.chargerSerialNumber,
        accessoriesType: Array.isArray(formValue.accessoriesType)
          ? formValue.accessoriesType
          : [formValue.accessoriesType],
        raiseAssetRequestFileName:
          formValue.raiseAssetRequestFileName instanceof File
            ? formValue.raiseAssetRequestFileName.name
            : formValue.raiseAssetRequestFileName,
        file:
          formValue.raiseAssetRequestFileName instanceof File
            ? formValue.raiseAssetRequestFileName
            : null,
        dateOfPickup: DateUtils.formatDateToString(formValue.dateOfPickup),
        employeeId: formValue.employeeId,
        cancellationReason: formValue.comments || '',
      },
    };
  }

  onSubmit() {
    if (this.requestForm.valid) {
      const formValue = this.requestForm.value;
      const leaderEmail = formValue.DnTLeader?.value || '';
      const managerEmail = formValue.reportingManager?.value || '';

      const ccEmailsWithLeaderManager = [...this.ccEmails];
      if (leaderEmail && !ccEmailsWithLeaderManager.includes(leaderEmail)) {
        ccEmailsWithLeaderManager.push(leaderEmail);
      }
      if (managerEmail && !ccEmailsWithLeaderManager.includes(managerEmail)) {
        ccEmailsWithLeaderManager.push(managerEmail);
      }

      const updatedFormValue = {
        ...formValue,
        ccEmailIds: ccEmailsWithLeaderManager,
      };

      const payload = this.getAssetRequestPayload(
        updatedFormValue,
        leaderEmail,
        managerEmail,
      );
      this.assetManagementService.createAssetRequest(payload).subscribe({
        next: () => {
          if (this.dialogRef) {
            this.dialogRef.close({ refreshTable: true });
          }
        },
        error: () => {},
      });
    } else {
      console.error(
        'Form validation failed on submit:',
        this.requestForm.errors,
        this.requestForm.value,
      );
      this.showValidationErrors();
    }
  }

  onCancel() {
    this.requestForm.reset();

    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  onClose() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private markFormGroupTouched() {
    for (const key of Object.keys(this.requestForm.controls)) {
      const control = this.requestForm.get(key);
      control?.markAsTouched();
    }
  }

  private showValidationErrors(): void {
    const invalidFields: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      dateOfPickup: 'Date of Pickup',
      location: 'Location',
      toEmailIds: 'TO Email',
      email: 'Resource Email',
      dateOfJoining: 'Date of Joining',
      firstName: 'First Name',
      lastName: 'Last Name',
      DnTLeader: 'D&T Leader',
      reportingManager: 'Reporting Manager',
      osType: 'OS Type',
      laptopType: 'Laptop Type',
      brandName: 'Brand Name',
      laptopSerialNumber: 'Laptop Serial Number',
      chargerSerialNumber: 'Charger Serial Number',
      status: 'Status',
      accessoriesType: 'Accessories Type',
      raiseAssetRequestFileName: 'File Upload',
      employeeId: 'Employee ID',
      comments: 'Comments',
    };

    Object.keys(this.requestForm.controls).forEach((key) => {
      const control = this.requestForm.get(key);
      if (control && control.invalid) {
        const label = fieldLabels[key] || key;
        invalidFields.push(label);
      }
    });

    if (invalidFields.length > 0) {
      const errorMessage = `Please fill the required fields: ${invalidFields.join(', ')}`;
      this.toastr.error(errorMessage, 'Validation Error');
    }
  }

  onLocationChange(locationOption: any): void {
    if (locationOption?.value) {
      this.loadEmailNotifications(locationOption.value);
    }
  }

  loadEmailNotifications(location: string): void {
    this.assetManagementService.getEmailNotifications(location).subscribe({
      next: (response) => {
        this.toEmails = response?.to || [];
        this.ccEmails = response?.cc || [];
        this.currentLocation = response?.location || location;
        if (this.requestForm) {
          this.requestForm.patchValue({
            toEmailIds: this.toEmails,
            ccEmailIds: this.ccEmails,
          });
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.toEmails = [];
        this.ccEmails = [];
        this.cdr.markForCheck();
      },
    });
  }

  removeToEmail(index: number): void {
    if (index >= 0 && index < this.toEmails.length) {
      this.toEmails.splice(index, 1);
      if (this.requestForm) {
        this.requestForm.patchValue({ toEmailIds: this.toEmails });
        this.requestForm.get('toEmailIds')?.updateValueAndValidity();
        this.requestForm.patchValue({
          toEmailIds: this.toEmails,
        });
      }
    }
  }

  removeCcEmail(index: number): void {
    if (index >= 0 && index < this.ccEmails.length) {
      this.ccEmails.splice(index, 1);
      if (this.requestForm) {
        this.requestForm.patchValue({
          ccEmailIds: this.ccEmails,
        });
      }
    }
  }

  onEditSubmit(): void {
    if (this.requestForm?.valid) {
      const formValue = this.requestForm.getRawValue();
      const leaderEmail =
        formValue.DnTLeader?.value || formValue.DnTLeader || '';
      const managerEmail =
        formValue.reportingManager?.value || formValue.reportingManager || '';
      const payload = this.getAssetRequestPayload(
        formValue,
        leaderEmail,
        managerEmail,
      );
      if (this.assetManagementService.updateAssetRequest) {
        this.assetManagementService.updateAssetRequest(payload).subscribe({
          next: () => {
            if (this.dialogRef) {
              this.dialogRef.close({ refreshTable: true });
            }
          },
          error: (error) => {
            let errorMsg = 'An error occurred while updating the request.';
            if (error?.error?.reason) {
              errorMsg = error.error.reason;
            } else if (error?.error?.message) {
              errorMsg = error.error.message;
            }
            this.toastr.error(errorMsg, 'Error');
          },
        });
      }
    } else {
      console.error(
        'Form validation failed on edit:',
        this.requestForm.errors,
        this.requestForm.value,
      );
      this.showValidationErrors();
    }
  }

  private async setupAccessoryOptions$(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.assetManagementService.getAllAccessoriesAfterUpdate(),
      );
      if (Array.isArray(response?.data) && response.data.length > 0) {
        this.accessoryOptions = response.data.map((item: any) => ({
          label: item?.accessoriesType ?? '',
          value: item?.accessoriesType ?? '',
        }));
      } else {
        this.accessoryOptions = this.localAccessoryOptions;
      }
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error fetching accessory options:', error);
      this.accessoryOptions = this.localAccessoryOptions;
      this.cdr.markForCheck();
    }
  }

  onEmployeeEmailOptionSelected(event: any): void {
    this.selectedEmployeeEmail = event.option?.value || null;
  }

  onEmployeeEmailButtonClick(): void {
    if (this.isEditMode) {
      return;
    }

    const emailToAdd = this.selectedEmployeeEmail;
    if (!emailToAdd) {
      this.duplicateEmailError = null;
      return;
    }
    if (
      this.toEmails.includes(emailToAdd) ||
      this.ccEmails.includes(emailToAdd)
    ) {
      this.duplicateEmailError = 'This email is already added to TO or CC.';
    } else {
      this.toEmails.push(emailToAdd);
      if (this.requestForm) {
        this.requestForm.patchValue({ toEmailIds: this.toEmails });
        this.requestForm.get('toEmailIds')?.updateValueAndValidity();
      }
      this.cdr.markForCheck();
      this.duplicateEmailError = null;
    }
    this.selectedEmployeeEmail = null;
    if (this.emailAutoInput) {
      this.emailAutoInput.nativeElement.value = '';
    }
  }

  onEmployeeEmailCcButtonClick(): void {
    if (this.isEditMode) {
      return;
    }

    const emailToAdd = this.selectedEmployeeEmail;
    if (!emailToAdd) {
      this.duplicateEmailError = null;
      return;
    }
    if (
      this.ccEmails.includes(emailToAdd) ||
      this.toEmails.includes(emailToAdd)
    ) {
      this.duplicateEmailError = 'This email is already added to TO or CC.';
    } else {
      this.ccEmails.push(emailToAdd);
      this.cdr.markForCheck();
      this.duplicateEmailError = null;
    }
    this.selectedEmployeeEmail = null;
    if (this.emailAutoInput) {
      this.emailAutoInput.nativeElement.value = '';
    }
  }

  async fetchAccessoriesDetails(
    osType: string = '',
    brandName: string = '',
    accessoriesTypeId: number = 1,
  ): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.assetManagementService.getAccessoriesDetails(
          osType,
          brandName,
          accessoriesTypeId,
        ),
      );

      let dataArray: any[] = [];
      if (Array.isArray(response?.data)) {
        dataArray = response.data;
      } else if (Array.isArray(response)) {
        dataArray = response;
      }

      if (dataArray.length > 0) {
        const mappedOptions = dataArray.map((item: string) => ({
          label: item,
          value: item,
        }));

        if (!osType && !brandName) {
          this.osTypeOptions = mappedOptions;
        } else if (osType && !brandName) {
          this.brandNameOptions = mappedOptions;
        } else if (osType && brandName) {
          this.laptopTypeOptions = mappedOptions;
        }

        this.cdr.markForCheck();
      } else {
        console.warn('No data received from accessories API');
      }
    } catch (error) {
      console.error('Error fetching accessories details:', error);
    }
  }

  onOsTypeChange(event: any): void {
    const selectedOsType = event?.value || event;

    this.requestForm.patchValue({
      brandName: '',
      laptopType: '',
    });
    this.brandNameOptions = [];
    this.laptopTypeOptions = [];

    if (selectedOsType) {
      this.fetchAccessoriesDetails(selectedOsType, '', 1);
    }
  }

  onBrandNameChange(event: any): void {
    const selectedBrandName = event?.value || event;
    const selectedOsType = this.requestForm.get('osType')?.value;
    this.requestForm.patchValue({
      laptopType: '',
    });
    this.laptopTypeOptions = [];
    if (selectedOsType && selectedBrandName) {
      this.fetchAccessoriesDetails(selectedOsType, selectedBrandName, 1);
    }
  }
}
